// MonthGrid — the calendar's visual surface. 7-col × 6-row grid (42
// cells) covering the visible month + leading/trailing days from
// adjacent months (standard calendar layout). Each cell renders the
// date number + rich post cards, with overflow indicator.
//
// Cards are GROUPED by (resolved post, scheduled_at) — a single post
// scheduled on IG + FB + TikTok at the same time renders as ONE card
// with three platform icons, not three separate rows. Clicking the card
// opens the edit modal for the FIRST underlying tile (per-platform edit
// is still per-tile, but the calendar surface stays clean).
//
// Click handlers:
//   - Empty area of a day cell → onDayClick (open create modal for that day)
//   - Tile card → onTileClick (open edit modal — receives the first tile
//                              of the group)
//   - Card's tiny ✕ button → onTileDelete (cancels the first tile of the
//                                          group; user can edit/delete
//                                          remaining platforms from the
//                                          edit modal)
//
// Today's cell gets a primary-tinted ring. Out-of-month cells get a
// muted tinting so the user can tell at a glance which days "belong"
// to the visible month.

import type { ScheduledPost } from '@/lib/api';
import type { Post } from '@/lib/gallery';
import { PLATFORM_META } from './platforms';

export interface DayCell {
  date: Date;
  /** YYYY-MM-DD local-time key — used by parent for grouping. */
  key: string;
  /** True when this cell's date is in the currently-visible month. */
  inMonth: boolean;
  isToday: boolean;
  /** Tiles sorted by scheduled_at ASC. Already grouped by parent. */
  tiles: ScheduledPost[];
}

interface MonthGridProps {
  cells: DayCell[];
  /** Indexed by post.id — used to render the tile thumbnail + headline.
   *  Missing entries (e.g. post was deleted but tile lingers) render as
   *  a "Missing post" placeholder. */
  postsById: Record<string, Post>;
  /** Indexed by post.id — signed (transformed) thumbnail URL. Missing
   *  entries (carousel zips, deleted posts, auto-pending placeholders)
   *  render an icon placeholder in the image area. */
  postImageUrls?: Record<string, string>;
  onDayClick: (date: Date) => void;
  onTileClick: (tile: ScheduledPost) => void;
  onTileDelete: (tile: ScheduledPost) => void;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Cap how many cards render fully in a cell. Cards are now grouped
// (multi-platform tiles for the same post merge), so 2 cards usually
// covers a normal user's day; overflow becomes a "+N more" affordance.
const CARDS_PER_CELL = 2;

// A grouped card — one or more tiles representing the same post scheduled
// at the same time across multiple platforms.
// Exported so AgendaList (mobile) can reuse the same card shape.
export interface TileGroup {
  /** Resolution key — either post_id, generated_post_id, or
   *  `placeholder:<tile_id>` for auto_generate tiles with no generated
   *  ad yet (those don't share a post so each gets its own group). */
  groupKey: string;
  /** The earliest tile in the group — used as the primary handle for
   *  click + delete callbacks. */
  primary: ScheduledPost;
  /** All tiles in the group, including the primary. */
  tiles: ScheduledPost[];
  /** Resolved post (if available) for title/type lookup. */
  post: Post | null;
}

/** Group a day's tiles by (post, scheduled_at_hour). Auto-pending
 *  placeholders never group with anything (each one is its own card)
 *  since the post doesn't exist yet to share.
 *  Exported for AgendaList (mobile) reuse. */
export function groupTilesForDay(
  tiles: ScheduledPost[],
  postsById: Record<string, Post>,
): TileGroup[] {
  const groups = new Map<string, TileGroup>();
  for (const tile of tiles) {
    const isAutoPending =
      tile.mode === 'auto_generate' && !tile.generated_post_id;
    const resolveKey = tile.post_id || tile.generated_post_id;
    const post = resolveKey ? postsById[resolveKey] ?? null : null;
    // Group key bundles "same post, same hour". Auto-pending tiles have
    // no shared post yet, but the IG + FB tiles of the same Ad Pilot slot
    // share the same (auto_batch_id, scheduled_at) — group on that so one
    // slot shows as ONE card with both platform badges (not two tiles).
    // Once rendered they share a generated_post_id and group naturally.
    const groupKey = isAutoPending
      ? `autopilot:${tile.auto_batch_id || 'x'}:${tile.scheduled_at}`
      : `${resolveKey || 'unknown'}:${tile.scheduled_at}`;
    const existing = groups.get(groupKey);
    if (existing) {
      existing.tiles.push(tile);
    } else {
      groups.set(groupKey, {
        groupKey,
        primary: tile,
        tiles: [tile],
        post,
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.primary.scheduled_at.localeCompare(b.primary.scheduled_at),
  );
}

export default function MonthGrid({
  cells,
  postsById,
  postImageUrls,
  onDayClick,
  onTileClick,
  onTileDelete,
}: MonthGridProps) {
  return (
    <div className="rounded-2xl glass-stroke bg-white/85 overflow-hidden border-l border-t border-surface-dark/40">
      {/* Weekday header row. Border-r on every column header gives crisp
          vertical separators that line up with the cells below. */}
      <div className="grid grid-cols-7 border-b border-surface-dark/40">
        {WEEKDAY_LABELS.map((d, i) => (
          <div
            key={d}
            className={`px-2 py-2 text-xs font-bold text-dark-muted uppercase tracking-wide text-center ${
              i < WEEKDAY_LABELS.length - 1 ? 'border-r border-surface-dark/40' : ''
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Month grid — 7 columns of days, rows auto-size to content.
          We DON'T force `grid-rows-6` because it makes every row span
          the same height (whichever's tallest), so an empty week still
          stretches to 240px just because another week has events.
          Auto-sized rows mean empty weeks collapse to the date row only,
          while weeks with events grow to fit their cards. Within a week,
          all cells still share the row's height (unavoidable in CSS
          grid) — that's intentional for calendar legibility. */}
      <div className="grid grid-cols-7 auto-rows-min">
        {cells.map((cell) => (
          <DayCellCard
            key={cell.key}
            cell={cell}
            postsById={postsById}
            postImageUrls={postImageUrls}
            onDayClick={onDayClick}
            onTileClick={onTileClick}
            onTileDelete={onTileDelete}
          />
        ))}
      </div>
    </div>
  );
}

function DayCellCard({
  cell,
  postsById,
  postImageUrls,
  onDayClick,
  onTileClick,
  onTileDelete,
}: {
  cell: DayCell;
  postsById: Record<string, Post>;
  postImageUrls?: Record<string, string>;
  onDayClick: (date: Date) => void;
  onTileClick: (tile: ScheduledPost) => void;
  onTileDelete: (tile: ScheduledPost) => void;
}) {
  const groups = groupTilesForDay(cell.tiles, postsById);
  const visibleGroups = groups.slice(0, CARDS_PER_CELL);
  const overflow = groups.length - visibleGroups.length;
  const hasEvents = groups.length > 0;

  // Past-day check (local) — past days don't get the click-to-schedule
  // affordance since the backend rejects past timestamps.
  const isPast = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return cell.date < today;
  })();

  // Cell background — three tones to communicate state at a glance:
  //   • in-month + has events → subtle surface tint (highlights activity)
  //   • in-month + empty       → pure white
  //   • out-of-month           → muted grey (sits back visually)
  const cellBg = !cell.inMonth
    ? 'bg-surface-dark/5'
    : hasEvents
      ? 'bg-surface/50'
      : 'bg-white';

  return (
    <div
      className={`
        group relative min-h-[116px] p-2 border-b border-r border-surface-dark/40
        ${cellBg}
        ${cell.isToday ? 'ring-2 ring-primary/40 ring-inset z-[1]' : ''}
      `}
    >
      {/* Date number row + add affordance. The add button is only
          visible on hover, and only for non-past days in-month. */}
      <div className="flex items-center justify-between mb-2">
        <span
          className={`
            inline-flex items-center justify-center w-6 h-6 text-xs font-bold
            ${cell.isToday
              ? 'rounded-full bg-primary text-white'
              : cell.inMonth
                ? 'text-dark'
                : 'text-dark-muted/40'}
          `}
        >
          {cell.date.getDate()}
        </span>
        {!isPast && cell.inMonth && (
          <button
            type="button"
            onClick={() => onDayClick(cell.date)}
            className="opacity-0 group-hover:opacity-100 hover:opacity-100 w-7 h-7 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-sm"
            title="Schedule a post for this day"
            aria-label="Schedule a post"
          >
            <i className="ri-add-line text-lg" />
          </button>
        )}
      </div>

      {/* Cards stack — 4px gap; compact horizontal pills don't need the
          generous spacing the old image-hero cards used. */}
      <div className="space-y-1">
        {visibleGroups.map((group) => (
          <TileCard
            key={group.groupKey}
            group={group}
            postImageUrls={postImageUrls}
            onClick={() => onTileClick(group.primary)}
            onDelete={() => onTileDelete(group.primary)}
          />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            onClick={() => onDayClick(cell.date)}
            className="w-full text-left text-xs text-dark-muted font-semibold px-2 py-1 rounded hover:bg-surface-dark/15 cursor-pointer"
          >
            +{overflow} more
          </button>
        )}
      </div>

      {/* Whole-cell click target overlays the empty space below the
          tiles — clicking ANY blank area of an in-month future day
          opens the create modal. Sized via flex-1; the actual click
          handler lives on the inner button so date number + cards
          above are not eaten. */}
      {!isPast && cell.inMonth && visibleGroups.length === 0 && (
        <button
          type="button"
          onClick={() => onDayClick(cell.date)}
          className="absolute inset-0 top-9 w-full opacity-0 hover:opacity-100 flex items-center justify-center text-xs text-dark-muted/50 font-semibold hover:bg-primary/5 transition-colors cursor-pointer"
          aria-label="Schedule a post for this day"
        >
          + add post
        </button>
      )}

    </div>
  );
}

/** Status indicator — color-coded text label for the tile's current state.
 *  Hierarchy is calm-by-default: gray for scheduled, in-progress states
 *  get amber (transitional), success goes green, failures go red. Avoids
 *  every-tile-shouting-for-attention on a calendar full of scheduled
 *  posts. */
function statusBadge(
  status: ScheduledPost['status'],
  isAutoPending: boolean,
  isGeneratingSoon = false,
): {
  label: string;
  className: string;
  icon: string;
} {
  if (isGeneratingSoon) {
    // Ad Pilot week-1 currently-rendering tiles. Worker is actively
    // running (immediate spawn on Schedule click). 10s poll on
    // /schedule will flip this to a real thumbnail within ~30-90s per
    // render.
    return {
      label: 'Generating',
      className: 'text-amber-700',
      icon: 'ri-loader-4-line animate-spin',
    };
  }
  if (isAutoPending) {
    // AI-pending tiles still aren't "alerts" — gray with a sparkle to
    // signal "AI will fill this slot" without screaming for attention.
    return {
      label: 'Pending AI',
      className: 'text-dark-muted',
      icon: 'ri-sparkling-2-line',
    };
  }
  switch (status) {
    case 'posted':
      return {
        label: 'Published',
        className: 'text-emerald-700',
        icon: 'ri-checkbox-circle-fill',
      };
    case 'failed':
      return {
        label: 'Failed',
        className: 'text-red-600',
        icon: 'ri-error-warning-fill',
      };
    case 'publishing':
      return {
        label: 'Publishing',
        className: 'text-amber-700',
        icon: 'ri-loader-4-line animate-spin',
      };
    case 'generating':
      return {
        label: 'Generating',
        className: 'text-amber-700',
        icon: 'ri-loader-4-line animate-spin',
      };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className: 'text-dark-muted/70',
        icon: 'ri-close-circle-line',
      };
    case 'pending':
    default:
      return {
        label: 'Scheduled',
        className: 'text-dark-muted',
        icon: 'ri-time-line',
      };
  }
}

/** Derive the post "type" tag from the post's storage path / template.
 *  .zip = carousel bundle (savePost convention from the wizard);
 *  everything else = single image post. AI-pending placeholders get
 *  their own tag. */
function typeTag(post: Post | null, isAutoPending: boolean): {
  label: string;
  className: string;
} {
  if (isAutoPending) {
    return {
      label: 'AI Ad',
      className: 'bg-accent/10 text-accent',
    };
  }
  if (post?.image_path?.endsWith('.zip')) {
    return {
      label: 'Carousel',
      className: 'bg-primary-light text-primary',
    };
  }
  return {
    label: 'Post',
    className: 'bg-surface-dark/30 text-dark-muted',
  };
}

function formatHour12(iso: string): string {
  const d = new Date(iso);
  // Show minutes only when non-zero — "2pm" for a round hour, "2:30pm" when a
  // specific minute was picked in the schedule modal's time picker.
  const opts: Intl.DateTimeFormatOptions = d.getMinutes() === 0
    ? { hour: 'numeric', hour12: true }
    : { hour: 'numeric', minute: '2-digit', hour12: true };
  return d
    .toLocaleTimeString(undefined, opts)
    .toLowerCase()
    .replace(' ', '');
}

export function TileCard({
  group,
  postImageUrls,
  onClick,
  onDelete,
}: {
  group: TileGroup;
  postImageUrls?: Record<string, string>;
  onClick: () => void;
  onDelete: () => void;
}) {
  const { primary, tiles, post } = group;
  const isAutoPending =
    primary.mode === 'auto_generate' && !primary.generated_post_id;

  // Distinguish "actively generating right now" from "waiting on a
  // future week". An Ad Pilot renders ONLY the CURRENT calendar week
  // immediately (fixed-duration; future weeks render on their own weekly
  // run). So the spinner belongs to auto-pending tiles in THIS week only
  // — a next-week tile is genuinely pending, not hung. The /schedule
  // page polls every 10s while any auto_generate tile is unfilled, so
  // the current-week render state is short-lived (~30-90s per render).
  const isGeneratingSoon = (() => {
    if (!isAutoPending) return false;
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(now.getDate() - now.getDay()); // back to Sunday
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7); // next Sunday (exclusive)
    const t = new Date(primary.scheduled_at);
    return t >= weekStart && t < weekEnd;
  })();

  const time = formatHour12(primary.scheduled_at);
  const status = statusBadge(primary.status, isAutoPending, isGeneratingSoon);

  // Card title — small subtext line. Auto-pending tiles get a distinct
  // label so users see "an AI ad will land here" before the worker
  // runs.
  const title = isAutoPending
    ? (isGeneratingSoon ? 'Generating now' : 'AI ad')
    : post?.title || post?.headline || (post ? 'Untitled post' : 'Missing post');

  // Resolve the thumbnail URL. Falls back to a placeholder icon when:
  //   - no post yet (auto-pending tile)
  //   - the post is a carousel (.zip — no per-frame thumbnail in v1)
  //   - the post was deleted but the tile lingers
  //   - URL sign failed (best-effort batch)
  const isCarousel = post?.image_path?.endsWith('.zip') ?? false;
  const thumbUrl = post && !isCarousel ? postImageUrls?.[post.id] : undefined;

  // Status-driven panel tint for the RIGHT side of the tile (where time
  // + status live). Image on the LEFT keeps its own colors. Reference:
  // emerald-50 for posted, lavender (accent-light) for scheduled,
  // amber-50 for in-progress, red-50 for failed. The right panel reads
  // as a "status badge" at a glance.
  const panelClass = (() => {
    if (primary.status === 'posted') return 'bg-emerald-100 text-emerald-800';
    if (primary.status === 'failed') return 'bg-red-100 text-red-800';
    if (primary.status === 'publishing' || primary.status === 'generating')
      return 'bg-amber-100 text-amber-800';
    if (primary.status === 'cancelled') return 'bg-surface-dark/30 text-dark-muted';
    // Actively rendering this week — amber tint signals "in motion".
    if (isGeneratingSoon) return 'bg-amber-50 text-amber-800';
    if (isAutoPending) return 'bg-accent-light text-accent';
    return 'bg-accent-light/60 text-dark'; // 'scheduled' default — lavender
  })();

  // Dedupe platforms across the group.
  const uniquePlatforms = Array.from(new Set(tiles.map((t) => t.platform)));

  const hoverTitle = isAutoPending
    ? `AI ad — ${uniquePlatforms.join(', ')} at ${time} (status: ${primary.status})`
    : `${title} — ${uniquePlatforms.join(', ')} at ${time} (status: ${primary.status})`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      title={hoverTitle}
      className="group/tile relative bg-white rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-all flex items-stretch overflow-hidden h-[88px] border border-surface-dark/30"
    >
      {/* Image — left ~40% of the tile. object-cover because these are
          DESIGNED creatives that fill their canvas (ads/posts), not
          product photos with empty backgrounds. Aspect-square would
          give a 88×88 image area at the current height. */}
      <div className={`relative w-[40%] flex-shrink-0 overflow-hidden ${
        isGeneratingSoon
          ? 'bg-gradient-to-br from-amber-50 via-amber-100/60 to-accent-light animate-pulse'
          : isAutoPending
            ? 'bg-gradient-to-br from-accent-light/70 to-accent-light/40'
            : 'bg-surface-dark/15'
      }`}>
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <i className={`${
              isGeneratingSoon
                ? 'ri-loader-4-line animate-spin text-amber-600'
                : isAutoPending
                  ? 'ri-sparkling-2-line text-accent'
                  : isCarousel
                    ? 'ri-stack-line text-dark-muted/60'
                    : 'ri-image-line text-dark-muted/50'
            } text-3xl`} />
            {isGeneratingSoon && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700/80">
                AI rendering
              </span>
            )}
          </div>
        )}
      </div>

      {/* Info panel — right ~60%, color-tinted by status. Stack:
          platform circles top-right · time middle · status bottom. */}
      <div className={`flex-1 min-w-0 flex flex-col justify-between px-3 py-2 ${panelClass}`}>
        {/* Platform circles row — top-right anchored. Each is a colored
            disc with the platform's white glyph inside. Up to 3 visible
            + numeric overflow chip. */}
        <div className="flex items-center justify-end gap-1">
          {uniquePlatforms.slice(0, 3).map((p) => {
            const m = PLATFORM_META[p] || PLATFORM_META.default;
            return (
              <span
                key={p}
                className="w-5 h-5 rounded-full flex items-center justify-center text-white shadow-sm"
                style={{ backgroundColor: m.color }}
                title={m.label}
                aria-label={m.label}
              >
                <i className={`${m.icon} text-[12px] leading-none`} />
              </span>
            );
          })}
          {uniquePlatforms.length > 3 && (
            <span className="w-5 h-5 rounded-full flex items-center justify-center bg-white/70 text-dark-muted text-[10px] font-bold shadow-sm">
              +{uniquePlatforms.length - 3}
            </span>
          )}
        </div>

        {/* Time — big bold focal point */}
        <div className="font-display font-bold text-xl leading-none truncate">
          {time}
        </div>

        {/* Status — uppercase chip-style label */}
        <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider leading-none">
          <i className={`${status.icon} text-[11px]`} />
          {status.label}
        </div>
      </div>

      {/* Hover-revealed cancel — floats INSIDE the tile's top-left corner (over
          the image). The tile root is overflow-hidden for the rounded image,
          so it must sit inside the bounds (top-1/left-1, not a negative offset)
          or it gets clipped. Kept clear of the platform circles (top-right of
          the panel) AND the calendar cell's "+" add button (top-right of the
          day) so nothing fights for the same corner. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover/tile:opacity-100 absolute top-1 left-1 w-5 h-5 rounded-full bg-white/95 backdrop-blur-sm border border-red-200 text-red-600 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center transition-all z-10"
        aria-label="Cancel"
        title="Cancel this scheduled post"
      >
        <i className="ri-close-line text-[11px] font-bold" />
      </button>
    </div>
  );
}
